/**
 * BookingCancelled - Page displayed when user cancels Stripe payment
 *
 * Shows a friendly message and provides options to:
 * - Return to the booking to try again
 * - Go back to the shop/freelancer page
 * - Contact support
 *
 * @file frontend/src/pages/customer/BookingCancelled.js
 * @created January 25, 2026
 */

import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";

const BookingCancelled = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = searchParams.get("booking_id");
  const cancelToken = searchParams.get("cancel_token");

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [restartPath, setRestartPath] = useState("/");
  const [reservationReleased, setReservationReleased] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const releasePendingBooking = async () => {
      if (!bookingId || !cancelToken) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/bookings/cancel-pending-checkout`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ bookingId, cancelToken }),
          },
        );

        if (response.ok) {
          const data = await response.json();
          setBooking(data.booking || null);
          setRestartPath(data.restartPath || "/");
          setReservationReleased(Boolean(data.released));
        }
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };

    releasePendingBooking();
  }, [bookingId, cancelToken]);

  // Styles
  const styles = {
    container: {
      minHeight: "60vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: isMobile ? "40px 20px" : "60px 20px",
      backgroundColor: "#f9fafb",
    },
    card: {
      backgroundColor: "#ffffff",
      borderRadius: "16px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      padding: isMobile ? "32px 24px" : "48px",
      maxWidth: "500px",
      width: "100%",
      textAlign: "center",
    },
    iconCircle: {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      backgroundColor: "#fef3c7",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 24px",
    },
    icon: {
      fontSize: "40px",
    },
    title: {
      fontSize: isMobile ? "22px" : "28px",
      fontWeight: "700",
      color: "#1f2937",
      marginBottom: "12px",
    },
    subtitle: {
      fontSize: isMobile ? "14px" : "16px",
      color: "#6b7280",
      lineHeight: "1.6",
      marginBottom: "32px",
    },
    bookingInfo: {
      backgroundColor: "#f3f4f6",
      borderRadius: "12px",
      padding: "16px",
      marginBottom: "24px",
      textAlign: "left",
    },
    bookingLabel: {
      fontSize: "12px",
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      marginBottom: "4px",
    },
    bookingValue: {
      fontSize: "14px",
      color: "#1f2937",
      fontWeight: "500",
    },
    buttonGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
    primaryButton: {
      backgroundColor: "#1a659e",
      color: "#ffffff",
      border: "none",
      borderRadius: "10px",
      padding: "14px 24px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s",
      textDecoration: "none",
      display: "block",
    },
    secondaryButton: {
      backgroundColor: "#ffffff",
      color: "#1a659e",
      border: "2px solid #1a659e",
      borderRadius: "10px",
      padding: "12px 24px",
      fontSize: "15px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s",
      textDecoration: "none",
      display: "block",
    },
    helpText: {
      marginTop: "24px",
      fontSize: "13px",
      color: "#9ca3af",
    },
    helpLink: {
      color: "#1a659e",
      textDecoration: "none",
    },
  };

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div style={styles.container}>
          <div style={{ fontSize: "16px", color: "#6b7280" }}>Loading...</div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  return (
    <>
      <CustomerHeader />
      <div style={styles.container}>
        <div style={styles.card}>
          {/* Warning Icon */}
          <div style={styles.iconCircle}>
            <span style={styles.icon}>⚠️</span>
          </div>

          {/* Title */}
          <h1 style={styles.title}>Payment Not Completed</h1>

          {/* Subtitle */}
          <p style={styles.subtitle}>
            Your payment was cancelled. {reservationReleased
              ? "Your pending time slot has been released, so you will need to choose it again to continue."
              : "No booking was completed."}{" "}
            You can try again whenever you're ready, or explore other available times.
          </p>

          {/* Important Notice */}
          <div
            style={{
              backgroundColor: "#fef3c7",
              border: "1px solid #fbbf24",
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "24px",
              textAlign: "left",
            }}
          >
            <p
              style={{
                fontSize: "13px",
                color: "#92400e",
                margin: 0,
                fontWeight: "500",
              }}
            >
              <strong>ℹ️ Important:</strong> {reservationReleased
                ? "Your unfinished checkout was cleared so the same Stripe payment page cannot be reused."
                : "Payment was not completed."}{" "}
              To secure your preferred time slot, start a fresh booking flow.
            </p>
          </div>

          {/* Booking Info (if available) */}
          {booking && (
            <div style={styles.bookingInfo}>
              {booking.serviceName && (
                <div style={{ marginBottom: "12px" }}>
                  <div style={styles.bookingLabel}>Service</div>
                  <div style={styles.bookingValue}>{booking.serviceName}</div>
                </div>
              )}
              {booking.appointmentDate && (
                <div style={{ marginBottom: "12px" }}>
                  <div style={styles.bookingLabel}>Date & Time</div>
                  <div style={styles.bookingValue}>
                    {new Date(booking.appointmentDate).toLocaleDateString(
                      "en-US",
                      {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )}{" "}
                    at {booking.startTime}
                  </div>
                </div>
              )}
              {booking.totalAmount && (
                <div>
                  <div style={styles.bookingLabel}>Total Amount</div>
                  <div style={styles.bookingValue}>
                    ${Number(booking.totalAmount).toFixed(2)}{" "}
                    {booking.currency?.toUpperCase() || "USD"}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={styles.buttonGroup}>
            <Link
              to="/"
              style={styles.primaryButton}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#155a8a")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#1a659e")}
            >
              Browse Services
            </Link>
            <button
              onClick={() => navigate(restartPath)}
              style={styles.secondaryButton}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f0f9ff";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#ffffff";
              }}
            >
              Choose Another Time
            </button>
          </div>

          {/* Help Text */}
          <p style={styles.helpText}>
            Having trouble?{" "}
            <a href="mailto:support@ogestyle.com" style={styles.helpLink}>
              Contact Support
            </a>
          </p>
        </div>
      </div>
      <CustomerFooter />
    </>
  );
};

export default BookingCancelled;
